/**
 * BADGE LIST REFRESH
 * ----------------------------------------------------------------------------------------------------
 * Works around stale product badges on collection/search cards.
 *
 * The collection (PLP) document is sometimes served from Shopify's shared page cache with outdated
 * `custom.badges` values. Re-fetching the same PLP is not reliable because it can return the same
 * stale data. The authoritative, always-fresh source for a product's badges is the product context
 * itself (the PDP is confirmed to always be correct).
 *
 * For each card this controller fetches a lightweight, badge-only section rendered in the product's
 * context via the Section Rendering API (`/products/{handle}?section_id=badges-fragment`) and swaps
 * only the badge markup. It is done per product, but kept cheap by:
 *   - lazily hydrating only cards near the viewport (IntersectionObserver),
 *   - limiting request concurrency,
 *   - de-duplicating and re-hydrating content re-rendered by faceting/pagination (MutationObserver),
 *   - preserving the current locale/market prefix so market-specific badges stay correct.
 *
 * On any failure the server-rendered badges are left untouched.
 */
(function () {
    "use strict";

    const BADGES_SELECTOR = "[data-badges][data-handle]";
    const FRAGMENT_SECTION_ID = "badges-fragment";
    const MAX_CONCURRENCY = 4;

    const queue = [];
    const seen = new WeakSet();
    const boxToTarget = new WeakMap();
    let active = 0;

    function getCollectionSection() {
        const container = document.querySelector("[data-collection]");
        return container ? container.closest(".shopify-section") : null;
    }

    function productSectionUrl(handle) {
        const root =
            (window.Shopify &&
                window.Shopify.routes &&
                window.Shopify.routes.root) ||
            "/";
        const url = new URL(
            `${root}products/${encodeURIComponent(handle)}`,
            window.location.origin
        );
        url.searchParams.set("section_id", FRAGMENT_SECTION_ID);
        return url.toString();
    }

    function hydrate(target) {
        const handle = target.getAttribute("data-handle");

        return fetch(productSectionUrl(handle), { cache: "no-store" })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(String(response.status));
                }
                return response.text();
            })
            .then((html) => {
                const doc = new DOMParser().parseFromString(html, "text/html");
                const fresh = doc.querySelector(BADGES_SELECTOR);

                if (!fresh) {
                    // Product has no badges: remove any stale ones.
                    if (target.childNodes.length) {
                        target.replaceChildren();
                    }
                    return;
                }

                if (target.innerHTML.trim() !== fresh.innerHTML.trim()) {
                    target.replaceChildren(
                        ...document.importNode(fresh, true).childNodes
                    );
                }
            })
            .catch(() => {
                /* Keep server-rendered badges on failure. */
            });
    }

    function pump() {
        while (active < MAX_CONCURRENCY && queue.length) {
            const target = queue.shift();
            active += 1;
            hydrate(target).then(() => {
                active -= 1;
                pump();
            });
        }
    }

    function enqueue(target) {
        if (seen.has(target) || !target.getAttribute("data-handle")) {
            return;
        }
        seen.add(target);
        queue.push(target);
        pump();
    }

    function observableFor(target) {
        // The [data-badges] wrapper uses `display: contents` and therefore generates no
        // layout box, so IntersectionObserver can never report it as intersecting. Observe
        // the nearest ancestor that actually generates a box (the product card) instead.
        return target.closest("product-card") || target.parentElement || target;
    }

    function observeTargets(observer, root) {
        root.querySelectorAll(BADGES_SELECTOR).forEach((target) => {
            if (seen.has(target)) {
                return;
            }
            if (observer) {
                const box = observableFor(target);
                boxToTarget.set(box, target);
                observer.observe(box);
            } else {
                enqueue(target);
            }
        });
    }

    function run() {
        const section = getCollectionSection();

        if (!section || !section.querySelector(BADGES_SELECTOR)) {
            return;
        }

        let observer = null;

        if ("IntersectionObserver" in window) {
            observer = new IntersectionObserver(
                (entries, obs) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            obs.unobserve(entry.target);
                            const target = boxToTarget.get(entry.target);
                            if (target) {
                                enqueue(target);
                            }
                        }
                    });
                },
                { rootMargin: "200px" }
            );
        }

        observeTargets(observer, section);

        // Re-hydrate cards re-rendered by faceting/pagination.
        new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) {
                        return;
                    }
                    if (node.matches && node.matches(BADGES_SELECTOR)) {
                        if (observer) {
                            const box = observableFor(node);
                            boxToTarget.set(box, node);
                            observer.observe(box);
                        } else {
                            enqueue(node);
                        }
                    }
                    if (node.querySelectorAll) {
                        observeTargets(observer, node);
                    }
                });
            });
        }).observe(section, { childList: true, subtree: true });
    }

    function schedule() {
        if ("requestIdleCallback" in window) {
            window.requestIdleCallback(run, { timeout: 1500 });
        } else {
            window.setTimeout(run, 300);
        }
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", schedule, { once: true });
    } else {
        schedule();
    }
})();
